import type { IconName } from '../../components/ui/Icon.js';

/**
 * Account configuration menu.
 *
 * Structured after the feature taxonomy a hosted-PBX customer expects — call
 * management, contact centre, media, numbers, reporting, billing — rather than
 * after our table layout. Customers look for "after-hours routing", not for the
 * table it happens to live in.
 *
 * `phase` marks sections whose backend does not exist yet. They are SHOWN
 * rather than hidden: the shape of the product is part of the sell, and an
 * honest "planned for Phase 2" beats a menu that quietly lacks half of what was
 * discussed. `permission` hides what this user may not touch.
 */
export interface ConfigItem {
  key: string;
  label: string;
  /** Set when there is no backend yet; renders a labelled placeholder. */
  phase?: string;
  permission?: string;
  /** Count shown as a pill, resolved by the page. */
}

export interface ConfigGroup {
  key: string;
  label: string;
  icon: IconName;
  items: ConfigItem[];
}

export const CONFIG_NAV: ConfigGroup[] = [
  {
    key: 'overview', label: 'Overview', icon: 'dashboard',
    items: [
      { key: 'dashboard', label: 'Dashboard' },
    ],
  },
  {
    key: 'people', label: 'Users & devices', icon: 'users',
    items: [
      { key: 'extensions', label: 'Extensions', permission: 'extension:read' },
      // Handsets are set up on each extension, under Phone setup.
      { key: 'departments', label: 'Departments', permission: 'extension:read' },
      // Everything about how a handset GETS its configuration lives here, and
      // only here (the provisioning module's portal half).
      { key: 'provisioning', label: 'Provisioning', permission: 'device:read' },
      // A handset model, its keys and its network, shared by many phones (docs/29).
      { key: 'templates', label: 'Phone templates', permission: 'device:read' },
      { key: 'portal-users', label: 'Portal users', permission: 'user:read' },
    ],
  },
  {
    // The client's own contacts (docs/32). Nobody above the account holds phonebook:read, so the
    // console never shows it to staff.
    key: 'phonebook', label: 'Phonebook', icon: 'contacts',
    items: [
      { key: 'contacts', label: 'Contacts', permission: 'phonebook:read' },
      { key: 'companies', label: 'Companies', permission: 'phonebook:read' },
    ],
  },
  {
    key: 'routing', label: 'Call routing', icon: 'ivr',
    items: [
      { key: 'inbound', label: 'Inbound routes', permission: 'number:read' },
      { key: 'ivrs', label: 'IVR menus', permission: 'flow:read' },
      { key: 'ring-groups', label: 'Hunt lists', permission: 'extension:read' },
      { key: 'queues', label: 'Queues', permission: 'queue:read' },
      // The words the workspace works from: why somebody paused, and what a call was.
      { key: 'agent-codes', label: 'Agent codes', permission: 'queue:read' },
      { key: 'schedules', label: 'Hours & schedules', permission: 'extension:read' },
      { key: 'holidays', label: 'Public holidays', permission: 'extension:read' },
      { key: 'outbound', label: 'Outbound routing', permission: 'outboundroute:read' },
      { key: 'dial-permissions', label: 'Dialling restrictions', permission: 'extension:read' },
    ],
  },
  {
    key: 'features', label: 'Voice features', icon: 'phone',
    items: [
      { key: 'voicemail', label: 'Voicemail', permission: 'voicemail:read' },
      { key: 'recording', label: 'Call recording', permission: 'recording:read' },
      { key: 'conference', label: 'Conference rooms', permission: 'queue:read' },
      { key: 'parking', label: 'Parking lots', permission: 'queue:read' },
      // Account-wide only. A person's own forwarding, screening and hot desking
      // are on their extension (docs/26 B3-B5).
      { key: 'screening', label: 'Blocked numbers', permission: 'extension:read' },
      { key: 'feature-codes', label: 'Feature codes', permission: 'tenant:read' },
    ],
  },
  {
    key: 'contact-centre', label: 'Contact centre', icon: 'queue',
    items: [
      { key: 'agents', label: 'Agents & skills', permission: 'queue:read' },
      // Shipped as Realtime -> Queues in the main console, which is where an
      // operator watching every account needs it. Kept here as a pointer.
      { key: 'wallboard', label: 'Live wallboard', phase: 'In Realtime', permission: 'queue:read' },
      { key: 'dispositions', label: 'Dispositions', phase: 'Phase 4', permission: 'queue:read' },
      { key: 'qa', label: 'Agent scoring', phase: 'Phase 4', permission: 'queue:read' },
    ],
  },
  {
    key: 'media', label: 'Media', icon: 'media',
    items: [
      { key: 'prompts', label: 'Prompts & greetings', permission: 'extension:read' },
      { key: 'moh', label: 'Music on hold', permission: 'extension:read' },
    ],
  },
  {
    // The people a campaign will call (docs/36 D1b). The client's own, like the phonebook: nobody
    // above the account holds dialler:read, so the console never shows it to staff.
    key: 'dialler', label: 'Dialler', icon: 'callOut',
    items: [
      { key: 'campaigns', label: 'Campaigns', permission: 'campaign:read' },
      { key: 'lead-lists', label: 'Lead lists', permission: 'dialler:read' },
      { key: 'do-not-call', label: 'Do not call', permission: 'dialler:read' },
    ],
  },
  {
    key: 'numbers', label: 'Numbers', icon: 'hash',
    items: [
      { key: 'numbers', label: 'Assigned numbers', permission: 'number:read' },
      { key: 'porting', label: 'Porting', phase: 'Phase 2', permission: 'port:read' },
    ],
  },
  {
    key: 'connectivity', label: 'Connectivity', icon: 'carrier',
    items: [
      // connectivity:read, not trunk:read: customers see this too -- which
      // carrier carries their calls and whether it is up. Staff get more.
      { key: 'trunks', label: 'Trunks', permission: 'connectivity:read' },
      { key: 'codecs', label: 'Codecs & media', phase: 'Phase 2', permission: 'trunk:read' },
    ],
  },
  {
    key: 'reporting', label: 'Reporting', icon: 'rates',
    items: [
      { key: 'calls', label: 'Call history', permission: 'cdr:read' },
      { key: 'analytics', label: 'Call analytics', phase: 'Phase 3', permission: 'report:read' },
      { key: 'queue-reports', label: 'Queue performance', permission: 'queue:read' },
      // Calls transcribed and analysed on the account's own keys (docs/37).
      { key: 'auto-qa', label: 'Auto QA', permission: 'qa:read' },
      { key: 'agent-reports', label: 'Agent activity', phase: 'Phase 3', permission: 'report:read' },
    ],
  },
  {
    key: 'billing', label: 'Billing', icon: 'billing',
    items: [
      { key: 'billing', label: 'Balance & ledger', permission: 'billing:read' },
      { key: 'invoices', label: 'Invoices', phase: 'Phase 1J', permission: 'invoice:read' },
      { key: 'usage', label: 'Usage breakdown', permission: 'billing:read' },
      { key: 'spend-controls', label: 'Spend controls', phase: 'Phase 1J', permission: 'creditlimit:write' },
    ],
  },
  {
    key: 'settings', label: 'Settings', icon: 'settings',
    items: [
      { key: 'profile', label: 'Company profile', permission: 'tenant:read' },
      { key: 'integrations', label: 'Integrations & webhooks', phase: 'Phase 4', permission: 'webhook:read' },
      { key: 'api-keys', label: 'API keys', permission: 'apikey:read' },
      { key: 'audit', label: 'Audit log', permission: 'audit:read' },
    ],
  },
];

/** Which group a section belongs to, so the right group opens on load. */
export function groupFor(itemKey: string): string | null {
  for (const g of CONFIG_NAV) {
    if (g.items.some((i) => i.key === itemKey)) return g.key;
  }
  return null;
}
